import React from 'react';
import { useSelector } from 'react-redux';
import { Copy, Help, Loading, SystemText } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import Chart from './chart';
import BarChart from './barChart';
import GroupPicker from 'components/admin/forms/groupPicker';
import MetricPicker from 'components/admin/forms/metricPicker';
import CompetencyPicker from 'components/admin/forms/competencyPicker';
import WidgetBase, { competencySet, groupSet, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import Correlation from '../components/correlation';

export const CompetencyAgainstMetric = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [flipped, setFlipped] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;

        if (prop === 'type1' && value) temp.metric1 = null;
        if (prop === 'type1' && !value) temp.competency1 = null;

        setValues(temp);
    };

    const height = defaultProps.height(item);

    const defaults = {};

    const st = (path, ...rest) => SystemText(`Dashboard/Widgets/CompetencyAgainstMetric/${path}`, ...rest);

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('General/Controls/GroupPicker/Error');
            validForm = false;
        }

        if (!competencySet(form.competency1)) {
            errors.competency1 = st('Fields/Competency/Error');
            validForm = false;
        }

        if (!metricSet(form.metric1)) {
            errors.metric1 = st('Fields/Metric/Error');
            validForm = false;
        }

        if (form.metric1?.subMetricId && (!form.metric1?.subMetricValues || form.metric1?.subMetricValues.length === 0)) {
            errors.metric1 = st('Fields/Metric/Error/SubMetric');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div
            onClick={() => setFlipped(!flipped)}
            className={`chart-container ${flipped ? 'flipped' : ''}`}
            id={`chart-${item.id}`}
            style={{ height: `${height}px` }}
        >
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart data={[data[1]]} item={item} id={`chart-${item.id}`} />
            </div>
            <div className="chart" style={{ height: `${height}px` }}>
                <BarChart data={[data[0]]} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        if (!recommendationData) return null;

        return <Correlation widget={item} value={recommendationData.correlation} />;
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <CompetencyPicker
                label={st('Fields/Competency/Label')}
                mandatory
                value={values.competency1}
                error={errors.competency1}
                onChange={value => updateProperty(value, 'competency1')}
            />
            <MetricPicker
                label={st('Fields/Metric/Label')}
                mandatory
                value={values.metric1}
                error={errors.metric1}
                onChange={value => updateProperty(value, 'metric1')}
                showAverage
            />
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default CompetencyAgainstMetric;
